import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchOldOrders, revertToNew, deleteOrder, deleteAllOldOrders, fetchOldOrdersTotals, fetchOldOrdersSummary } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { readyOrderUnits,formatPhoneWithCaseStyle } from "../../utils/orderLineDisplay";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify"; // Import react-toastify
import "react-toastify/dist/ReactToastify.css"; // Import toastify CSS

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const OldOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const { data: totals, isLoading: isLoadingTotals, error: errorTotals } = useQuery({
    queryKey: ["oldOrdersTotals"],
    queryFn: fetchOldOrdersTotals,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { totalProfit: 0, totalAmount: 0, totalProductsSold: 0 },
  });

  const [oldOrdersSummary, setOldOrdersSummary] = useState({ count: 0, totalProfit: 0, totalAmount: 0 });
  const { data: oldOrdersSummaryData, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["oldOrdersSummary"],
    queryFn: fetchOldOrdersSummary,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  useEffect(() => {
    if (oldOrdersSummaryData) {
      setOldOrdersSummary(oldOrdersSummaryData);
    }
  }, [oldOrdersSummaryData]);

  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const isLoading = isLoadingTotals || isLoadingSummary;
  const queryError = errorTotals || errorSummary;

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        const enrichedItems = await Promise.all(
          order.orderDetails.map(async (detail) => {
            const readyUnits = await readyOrderUnits(detail);
            if (readyUnits) {
              if (readyUnits.length === 0) return null;
              return { ...readyUnits[0], quantity: detail.quantity, price: detail.price, matchCount: 0 };
            }
            const design = await fetchDesignById(detail.designId);
            const type = await fetchTypeById(detail.typeId);
            if (!design || !type) return null;
            return {
              id: detail.designId,
              name: design.Name,
              quantity: detail.quantity,
              price: detail.price,
              type: formatPhoneWithCaseStyle(type.name, detail),
              driveLink: design.driveLink || "",
            };
          })
        ).then((items) => items.filter((item) => item !== null));
        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchOldOrders(0, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchOldOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleRevertToNew = async (orderId) => {
    const confirmRevert = window.confirm(`هل أنت متأكد من إعادة الطلب رقم ${orderId} إلى حالة جديد؟`);
    if (!confirmRevert) return;
    try {
      await revertToNew(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      toast.success(`تم إعادة الطلب رقم ${orderId} إلى حالة جديد بنجاح!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      console.error("Error reverting order to new:", err);
      const errorMessage = "فشل في إعادة الطلب إلى حالة جديد. يرجى المحاولة مرة أخرى.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!isAdmin(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      console.error("Error deleting order:", err);
      const errorMessage = "فشل في حذف الطلب. يرجى المحاولة مرة أخرى.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleDeleteAllOrders = async () => {
    if (!isAdmin(authState.role)) return;
    const confirmDeleteAll = window.confirm("هل أنت متأكد من حذف جميع الطلبات القديمة؟ هذا الإجراء لا يمكن التراجع عنه.");
    if (!confirmDeleteAll) return;
    try {
      await deleteAllOldOrders();
      setLoadedOrders([]);
      setHasMore(false);
      setOldOrdersSummary({ count: 0, totalProfit: 0, totalAmount: 0 });
      toast.success("تم حذف جميع الطلبات القديمة بنجاح!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      console.error("Error deleting all old orders:", err);
      const errorMessage = "فشل في حذف جميع الطلبات القديمة. يرجى المحاولة مرة أخرى.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <ToastContainer /> {/* Add ToastContainer to render toasts */}
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">الطلبات القديمة</h2>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            رجوع
          </button>
        </div>
        {(error || queryError) && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
            {error || "فشل في تحميل بيانات الطلبات القديمة. يرجى المحاولة مرة أخرى."}
          </div>
        )}
        {isAdmin(authState.role) && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-2">ملخص الطلبات القديمة</h2>
                {isLoading ? (
                  <SkeletonLoader type="card" />
                ) : (
                  <div>
                    <p>عدد الطلبات: {oldOrdersSummary.count}</p>
                    <p>بدون الشحن: LE {oldOrdersSummary.totalProfit}</p>
                    <p>إجمالي المبلغ: LE {oldOrdersSummary.totalAmount}</p>
                  </div>
                )}
              </div>
              {/* <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-2">إجمالي المنتجات المباعة</h2>
                {isLoading ? (
                  <SkeletonLoader type="productsCard" />
                ) : (
                  <p>إجمالي المنتجات المباعة: {totals.totalProductsSold}</p>
                )}
              </div> */}
            </div>
            <div className="mb-6">
              <button
                onClick={handleDeleteAllOrders}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                حذف جميع الطلبات القديمة
              </button>
            </div>
          </>
        )}
        <div className="bg-white shadow-md rounded-lg p-6">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {loadedOrders.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-1 text-right font-semibold">رقم الطلب</th>
                          <th className="p-1 text-right font-semibold">اسم العميل</th>
                          <th className="p-1 text-right font-semibold">رقم الهاتف</th>
                          <th className="p-1 text-right font-semibold">الكمية</th>
                          <th className="p-1 text-right font-semibold">المنتجات</th>
                          <th className="p-1 text-right font-semibold">رابط الدرايف</th>
                          <th className="p-1 text-right font-semibold">الإجمالي</th>
                          <th className="p-1 text-right font-semibold">قبل التوصيل</th>
                          <th className="p-1 text-right font-semibold">التاريخ</th>
                          <th className="p-1 text-right font-semibold">العنوان</th>
                          <th className="p-1 text-right font-semibold">ملاحظات</th>
                          <th className="p-1 text-right font-semibold">إجراءات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadedOrders.map((order) => (
                          <tr key={order.orderNumber} className="border-b hover:bg-gray-100">
                            <td className="p-2 text-right">{order.orderNumber}</td>
                            <td className="p-2 text-right">{order.name}</td>
                            <td className="p-2 text-right">{order.phoneNumber}-{order.phoneNumber2}</td>
                            <td className="p-2 text-right">
                              {order.items.map((item) => (
                                <div key={item.id} className="mb-2">
                                  ({item.quantity})
                                  <p>----</p>
                                </div>
                              ))}
                            </td>
                            <td className="p-2 text-right">
                              {order.items.map((item) => (
                                <div key={item.id} className="mb-2">
                                  {item.name} {item.type}
                                  <p>---- </p>
                                </div>
                              ))}
                            </td>
                            <td className="p-2 text-right">
                              {order.items.map((item) => (
                                <div key={item.id} className="mb-2">
                                  {item.driveLink ? (
                                    <>
                                      <a
                                        href={item.driveLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline"
                                      >
                                        رابط الدرايف
                                      </a>
                                      <br />
                                      <span>{item.id}</span>
                                    </>
                                  ) : (
                                    "لا يوجد رابط"
                                  )}
                                  <p>----</p>
                                </div>
                              ))}
                            </td>
                            <td className="p-2 text-right">LE {order.total}</td>
                            <td className="p-2 text-right">LE {order.profit}</td>
                            <td className="p-2 text-right">{order.date}</td>
                            <td className="p-2 text-right">{order.address}</td>
                            <td className="p-2 text-right">{order.note || "لا توجد ملاحظات"}</td>
                            <td className="p-2 text-right flex flex-col">
                              {canAct ? (
                              <>
                              <button
                                onClick={() => handleRevertToNew(order.orderNumber)}
                                className="bg-green-500 text-white rounded hover:bg-green-600"
                                style={{ padding: '4px 8px', marginBottom: '4px', marginRight: '4px' }}
                              >
                                إعادة إلى جديد
                              </button>
                              {isAdmin(authState.role) && (
                                <button
                                  onClick={() => handleDeleteOrder(order.orderNumber)}
                                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                                >
                                  حذف
                                </button>
                              )}
                              </>
                              ) : (
                                <span className="text-sm text-slate-500">عرض فقط</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {hasMore && (
                      <div className="text-center mt-4">
                        <button
                          onClick={loadMoreOrders}
                          disabled={isLoadingMore}
                          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                        >
                          {isLoadingMore ? "جاري التحميل..." : "تحميل المزيد"}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-center">لا توجد طلبات قديمة</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OldOrdersPage;